/**
 * HeroContent
 * ----------------------------------------------------------------
 * All the text + buttons that sit on top of the liquid canvas.
 * Kept in its own file so copy / typography tweaks are easy.
 *
 * Font color: warm cream / ivory (#fff3dc) so the type pops
 * against the rippling water surface while still feeling
 * earthy and in-scene. A soft brown glow underneath keeps
 * everything readable through the bright reflections.
 */

type Props = {
  time: string;
};

const CREAM = "#fff3dc";
const CREAM_SOFT = "rgba(255, 243, 220, 0.92)";
const CREAM_MUTED = "rgba(255, 243, 220, 0.75)";
const DEEP_BROWN = "#2a160a";

// Soft warm glow that improves contrast against the water
// without producing a hard drop-shadow look.
const TEXT_GLOW =
  "0 1px 2px rgba(42, 22, 10, 0.55), 0 2px 14px rgba(42, 22, 10, 0.35)";

export default function HeroContent({ time }: Props) {
  return (
    <div
      className="relative z-10 h-full w-full flex flex-col"
      style={{ color: CREAM, textShadow: TEXT_GLOW }}
    >
      {/* top label row */}
      <div
        className="flex items-center justify-between px-6 md:px-10 pt-24 text-xs tracking-[0.25em] uppercase"
        style={{ color: CREAM_SOFT }}
      >
        <span className="flex items-center gap-2">
          <span
            className="w-2 h-2 rounded-full animate-pulse"
            style={{ background: CREAM }}
          />
          Live · Berlin {time}
        </span>
        <span className="hidden md:inline">Vol. 04 — 2026 Showreel</span>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center text-center px-6">
        <span
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border backdrop-blur-md text-xs tracking-widest uppercase whitespace-nowrap"
          style={{
            color: CREAM,
            borderColor: "rgba(255, 243, 220, 0.55)",
            background: "rgba(42, 22, 10, 0.25)",
            textShadow: "none",
          }}
        >
          <i className="ri-sparkling-2-fill" style={{ color: CREAM }} />
          A motion design studio
        </span>

        <h1
          className="mt-6 font-medium leading-[0.95] tracking-tight"
          style={{
            fontFamily: "'Instrument Serif', Georgia, serif",
            color: CREAM,
          }}
        >
          <span className="block text-5xl sm:text-7xl md:text-8xl lg:text-[9rem]">
            Shapes that
          </span>
          <span className="block text-5xl sm:text-7xl md:text-8xl lg:text-[9rem] italic">
            pour & flow.
          </span>
        </h1>

        <p
          className="mt-8 max-w-xl text-base md:text-lg leading-relaxed"
          style={{ color: CREAM_SOFT }}
        >
          We craft fluid identities, kinetic brand systems, and chromed
          interfaces for forward-thinking teams. Real metal, real light,
          real motion.
        </p>

        <div className="mt-10 flex flex-col sm:flex-row items-center gap-3">
          <a
            href="#work"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-medium text-sm whitespace-nowrap cursor-pointer transition-colors"
            style={{
              background: CREAM,
              color: DEEP_BROWN,
              textShadow: "none",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLAnchorElement).style.background =
                "#ffe7bf";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLAnchorElement).style.background = CREAM;
            }}
          >
            See selected work
            <i className="ri-arrow-right-line" />
          </a>
          <a
            href="#process"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full border backdrop-blur-md font-medium text-sm whitespace-nowrap cursor-pointer transition-colors"
            style={{
              color: CREAM,
              borderColor: "rgba(255, 243, 220, 0.55)",
              background: "rgba(42, 22, 10, 0.2)",
            }}
          >
            <i className="ri-play-circle-line text-lg" />
            Watch showreel · 1:24
          </a>
        </div>
      </div>

      {/* bottom stats row */}
      <div className="px-6 md:px-10 pb-8 grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { k: "Studios", v: "Berlin · Lisbon" },
          { k: "Founded", v: "2019" },
          { k: "Awards", v: "27 international" },
          { k: "Avg. project", v: "6 weeks" },
        ].map((s) => (
          <div
            key={s.k}
            className="flex flex-col gap-1 pt-3 border-t"
            style={{ borderColor: "rgba(255, 243, 220, 0.35)" }}
          >
            <span
              className="text-[10px] tracking-[0.25em] uppercase"
              style={{ color: CREAM_MUTED }}
            >
              {s.k}
            </span>
            <span
              className="text-sm md:text-base font-medium"
              style={{ color: CREAM }}
            >
              {s.v}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}